import { Button, IconButton } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { markAsWixStudioAccount } from '../../../api/StudioWorkspaceService/markAsWixStudioAccount'
import { unmarkAsWixStudioAccount } from '../../../api/StudioWorkspaceService/unmarkAsWixStudioAccount'
import { usePageLogger } from '../../../bi/mfi'
import WixStudioLogo from '../../reusable/icons/WixStudioLogo.svg'
import { RichText } from '../../reusable/RichText'
import Icon from './icon.svg'
import type { TWixStudioSplit as Content } from './WixStudioSplitV2.content'
import { wixStudioSplitV2DataHooks as dataHooks } from './WixStudioSplitV2.dataHooks'
import s from './WixStudioSplitV2.scss'

export const WixStudioSplitV2: FC = () => {
  const [searchParams] = useSearchParams()
  const [isPageLoading] = usePageLoading()
  const next = useNext()
  const { content } = useContent<Content>()
  const { question, secondaryQuestion } = content
  const pageLogger = usePageLogger()

  const authMethod = searchParams.get('authMethod')
  const isDisclaimerVisible = content.disclaimer && authMethod === 'GOOGLE_ONE_TAP'

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const handleClick =
    (questionKey: string, optionKey: string, markAsStudio: boolean, unmarkAsStudio: boolean) => () => {
      if (isPageLoading) {
        return
      }
      if (markAsStudio) {
        void markAsWixStudioAccount()
      } else if (unmarkAsStudio) {
        void unmarkAsWixStudioAccount()
      }

      void pageLogger.answer({ answer: optionKey })
      next({
        [questionKey]: { values: [optionKey] },
      })
    }

  const secondaryOption = secondaryQuestion?.options[0]

  return (
    <div className={s.WixStudioSplitV2}>
      <WixStudioLogo className={s.WixStudioSplitV2__logo} />
      <div className={s.WixStudioSplitV2__content}>
        <div className={s.WixStudioSplitV2__welcomeLine} data-hook={dataHooks.title()}>
          {content.question.content.title}
        </div>
        <div className={s.WixStudioSplitV2__questionLine}>{content.question.content.subtitle}</div>
        <div className={s.WixStudioSplitV2__options}>
          {question.options.map(option => (
            <div
              data-hook={dataHooks.option({ key: option.optionKey })}
              key={option.optionKey}
              className={s.WixStudioSplitV2__option}
              style={{ backgroundColor: option.backgroundColor }}
              onClick={handleClick(question.questionKey, option.optionKey, option.markAsStudio, option.unmarkAsStudio)}
            >
              <img className={s.WixStudioSplitV2__graphics} src={option.img.content.src} alt={option.img.content.alt} />
              <div className={s.WixStudioSplitV2__caption}>
                <div className={s.WixStudioSplitV2__optionTitle}>{option.label}</div>
                <div className={s.WixStudioSplitV2__optionSubtitle}>{option.description}</div>
                <Button className={s.WixStudioSplitV2__button}>
                  <span>{option.btn}</span>
                  <Icon />
                </Button>
              </div>
            </div>
          ))}
        </div>
        {secondaryOption && (
          <div className={s.WixStudioSplitV2__secondaryLinkContainer}>
            <span
              data-hook={dataHooks.secondaryOption({ key: secondaryOption.optionKey })}
              onClick={handleClick(
                secondaryQuestion.questionKey,
                secondaryOption.optionKey,
                secondaryOption.markAsStudio,
                secondaryOption.unmarkAsStudio,
              )}
              className={s.WixStudioSplitV2__secondaryLink}
            >
              <span>{secondaryOption.label}</span>
              <Icon />
            </span>
          </div>
        )}
      </div>
      {isDisclaimerVisible && (
        <div className={s.WixStudioSplitV2__footer}>
          <RichText className={s.WixStudioSplitV2__disclaimer} dataHook={dataHooks.disclaimer()}>
            {content.disclaimer}
          </RichText>
        </div>
      )}
    </div>
  )
}
